export type TutorialStep = {
  id: string;
  title: string;
  text: string;
  /** Optional condition — when true, auto-advance after short delay */
  check?: (ctx: TutorialContext) => boolean;
  hint?: string;
  /** Freeze opponent AI */
  freezeOpp?: boolean;
  allowNext?: boolean;
};

export type TutorialContext = {
  advanced: boolean;
  retreated: boolean;
  lunged: boolean;
  parried: boolean;
  cameraSwitched: boolean;
  scored: boolean;
  playerX: number;
  startX: number;
};

const STEPS: TutorialStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to the Academy',
    text: 'I am your maître d’armes. We fence foil — the most technical weapon. Valid target is the metallic lamé on the torso. First, feel the strip beneath your feet.',
    allowNext: true,
    freezeOpp: true,
  },
  {
    id: 'en_garde',
    title: 'En Garde',
    text: 'You stand en garde: feet angled, weapon arm extended, off-hand for balance. The piste is a narrow strip — stay on it. Only forward and back matter.',
    allowNext: true,
    freezeOpp: true,
    hint: 'Notice the metallic piste and end lines in red.',
  },
  {
    id: 'advance',
    title: 'Advance',
    text: 'Press D or → to advance toward your opponent. Keep your guard — do not rush blindly.',
    check: (c) => c.advanced,
    freezeOpp: true,
    hint: 'Hold D to step forward along the strip',
  },
  {
    id: 'retreat',
    title: 'Retreat',
    text: 'Press A or ← to retreat. Distance is everything in foil — control the measure (distance to hit with a lunge).',
    check: (c) => c.retreated,
    freezeOpp: true,
    hint: 'Hold A to step back',
  },
  {
    id: 'lunge',
    title: 'The Lunge',
    text: 'Press Space (or W) to lunge. This is your primary attack. The front foot drives, the arm extends, the tip seeks the lamé.',
    check: (c) => c.lunged,
    freezeOpp: true,
    hint: 'Press Space to lunge',
  },
  {
    id: 'priority',
    title: 'Right of Way',
    text: 'Foil uses priority (right of way). Whoever starts a clear attack first owns the phrase. If both land, the fencer with priority scores. This is the heart of foil.',
    allowNext: true,
    freezeOpp: true,
  },
  {
    id: 'parry',
    title: 'Parry',
    text: 'Q = high parry (sixte), E = low parry (quarte). A successful parry steals priority — then press R to riposte!',
    check: (c) => c.parried,
    freezeOpp: false,
    hint: 'When the coach attacks, press Q or E to parry',
  },
  {
    id: 'target',
    title: 'Valid Target',
    text: 'Only the conductive lamé (metallic jacket) scores. Arms, legs, mask bib (foil), and floor are off-target — white light, no point, phrase stops.',
    allowNext: true,
    freezeOpp: true,
  },
  {
    id: 'cameras',
    title: 'Cameras',
    text: 'Switch views anytime: 1 Side (classic strip), 2 Third-person, 3 FPS inside the mask. Press C to cycle. Try each now.',
    check: (c) => c.cameraSwitched,
    allowNext: true,
    freezeOpp: true,
    hint: 'Press 1, 2, 3 or C to change camera',
  },
  {
    id: 'score',
    title: 'Score a Touch',
    text: 'Land a valid touch on your opponent’s lamé. Watch the scoring machine lights — green for you, red for them.',
    check: (c) => c.scored,
    freezeOpp: false,
    hint: 'Close distance, lunge to the torso',
  },
  {
    id: 'done',
    title: 'You Are Ready',
    text: 'Allez! You know the measure, the lunge, priority, and the parry-riposte. Fence a full bout to five — and remember: calm hand, sharp mind.',
    allowNext: true,
    freezeOpp: true,
  },
];

export class TutorialDirector {
  index = 0;
  ctx: TutorialContext = {
    advanced: false,
    retreated: false,
    lunged: false,
    parried: false,
    cameraSwitched: false,
    scored: false,
    playerX: 0,
    startX: 0,
  };
  private autoTimer = 0;
  active = false;

  start(playerX: number): void {
    this.active = true;
    this.index = 0;
    this.autoTimer = 0;
    this.ctx = {
      advanced: false,
      retreated: false,
      lunged: false,
      parried: false,
      cameraSwitched: false,
      scored: false,
      playerX,
      startX: playerX,
    };
  }

  stop(): void {
    this.active = false;
  }

  get step(): TutorialStep {
    return STEPS[Math.min(this.index, STEPS.length - 1)];
  }

  get progress(): number {
    return (this.index / (STEPS.length - 1)) * 100;
  }

  get isComplete(): boolean {
    return this.index >= STEPS.length - 1 && this.step.id === 'done';
  }

  get totalSteps(): number {
    return STEPS.length;
  }

  noteAdvance(): void {
    this.ctx.advanced = true;
  }
  noteRetreat(): void {
    this.ctx.retreated = true;
  }
  noteLunge(): void {
    this.ctx.lunged = true;
  }
  noteParry(): void {
    this.ctx.parried = true;
  }
  noteCamera(): void {
    this.ctx.cameraSwitched = true;
  }
  noteScore(): void {
    this.ctx.scored = true;
  }

  update(dt: number): 'stay' | 'advanced' | 'finished' {
    if (!this.active) return 'stay';
    const step = this.step;
    if (step.check?.(this.ctx)) {
      this.autoTimer += dt;
      if (this.autoTimer > 0.55) {
        this.autoTimer = 0;
        return this.next();
      }
    }
    return 'stay';
  }

  next(): 'advanced' | 'finished' {
    if (this.index >= STEPS.length - 1) {
      this.active = false;
      return 'finished';
    }
    this.index += 1;
    this.autoTimer = 0;
    if (this.index >= STEPS.length - 1) return 'advanced';
    return 'advanced';
  }

  skip(): void {
    this.active = false;
  }
}
